/* Revivr — recherche sur mesure.
   Une barre flottante sur le hero ouvre un panneau où le client décrit ce qu'il
   cherche ; le serveur renvoie les critères compris et les pièces les plus proches.
   Aucune dépendance. Config optionnelle : window.RevivrAI = { endpoint, collectionUrl } */
(() => {
  'use strict';
  const CFG = Object.assign({ endpoint: '/api/ai-search', collectionUrl: '/collections/tout-voir-1', param: 'ia' }, window.RevivrAI || {});
  const hero = document.querySelector('.index-section--hero');
  if (!hero || document.querySelector('.rvai-trigger')) return;

  const EXAMPLES = ['un col V bleu marine en M', 'un col roulé beige bien chaud', 'un cardigan Eric Bompard', 'un cadeau à moins de 80 €', 'du rose poudré, taille S'];
  const IDEAS = ['Un col V bleu marine en taille M', 'Un col roulé bien chaud pour l’hiver, taille S', 'Un cardigan Eric Bompard', 'Un cadeau à moins de 80 € en 38', 'Quelque chose de coloré en taille L', 'Un pull gris clair ras du cou'];
  const HINTS = [['Couleur et nuance', '« bleu marine », « rose poudré », « tons sobres »'], ['Forme', 'col V, col roulé, ras du cou, cardigan…'], ['Taille, marque, budget', '« en 38 », « Eric Bompard », « moins de 90 € »']];
  const PAGE = 12;
  const SPARK = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M10 4C10.7 9.2 13.3 11.8 18.5 12.5 13.3 13.2 10.7 15.8 10 21 9.3 15.8 6.7 13.2 1.5 12.5 6.7 11.8 9.3 9.2 10 4ZM19 1.5C19.3 3.7 20.3 4.7 22.5 5 20.3 5.3 19.3 6.3 19 8.5 18.7 6.3 17.7 5.3 15.5 5 17.7 4.7 18.7 3.7 19 1.5Z"/></svg>';
  const ARROW = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13.5 6.5 19 12l-5.5 5.5"/></svg>';
  const CROSS = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" d="M6 6l12 12M18 6 6 18"/></svg>';

  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const el = html => { const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; };
  const euros = n => `${Number.isInteger(n) ? n : n.toFixed(2).replace('.', ',')} €`;
  const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ───────────── Barre flottante sur le hero ─────────────
  const trigger = el(`
    <button type="button" class="rvai-trigger" aria-haspopup="dialog" aria-label="Recherche sur mesure : décrivez le cachemire que vous cherchez">
      <span class="rvai-trigger__icon">${SPARK}</span>
      <span class="rvai-trigger__text">Décrivez le cachemire que vous cherchez…</span>
      <span class="rvai-trigger__go">${ARROW}</span>
    </button>`);
  hero.classList.add('rvai-host');
  hero.append(trigger);
  trigger.addEventListener('click', () => open());

  // Exemples qui s'écrivent tout seuls, pour montrer qu'on peut parler naturellement
  const typed = trigger.querySelector('.rvai-trigger__text');
  let typing = null;
  function typeLoop() {
    if (calm) return;
    let i = 0, n = 0, dir = 1;
    const step = () => {
      const word = EXAMPLES[i];
      n += dir;
      typed.textContent = `Je cherche ${word.slice(0, n)}`;
      typed.classList.add('is-typing');
      let wait = dir > 0 ? 55 : 22;
      if (dir > 0 && n === word.length) { dir = -1; wait = 2400; }
      else if (dir < 0 && n === 0) { dir = 1; i = (i + 1) % EXAMPLES.length; wait = 350; }
      typing = setTimeout(step, document.hidden || isOpen ? 1500 : wait);
    };
    typing = setTimeout(step, 2800);
  }
  typeLoop();

  // ───────────── Panneau de recherche ─────────────
  let panel, input, body, chipsBox, statusBox, sizesBox, clearBtn;
  let isOpen = false, ctrl = null, debounce = null, skeletonTimer = null;
  let state = { data: null, shown: PAGE, catalogSize: null };

  function build() {
    panel = el(`
      <div class="rvai" role="dialog" aria-modal="true" aria-labelledby="rvai-title" hidden>
        <div class="rvai__inner">
          <header class="rvai__head">
            <p class="rvai__eyebrow" id="rvai-title">${SPARK}<span>Recherche sur mesure</span></p>
            <button type="button" class="rvai__close" aria-label="Fermer la recherche"><span>Fermer</span>${CROSS}</button>
          </header>
          <form class="rvai__form" role="search" novalidate>
            <label class="rvai__label" for="rvai-q">Décrivez la pièce que vous cherchez</label>
            <div class="rvai__field">
              <textarea id="rvai-q" class="rvai__input" rows="1" maxlength="300" autocomplete="off" autocapitalize="sentences" spellcheck="false" enterkeyhint="search" placeholder="Un col V bleu marine en taille M…"></textarea>
              <button type="button" class="rvai__clear" hidden>Effacer</button>
              <button type="submit" class="rvai__submit" aria-label="Lancer la recherche">${ARROW}</button>
            </div>
          </form>
          <div class="rvai__chips" hidden></div>
          <p class="rvai__status" role="status" aria-live="polite"></p>
          <div class="rvai__sizes" hidden></div>
          <div class="rvai__body"></div>
        </div>
      </div>`);
    document.body.append(panel);
    input = panel.querySelector('.rvai__input');
    body = panel.querySelector('.rvai__body');
    chipsBox = panel.querySelector('.rvai__chips');
    statusBox = panel.querySelector('.rvai__status');
    sizesBox = panel.querySelector('.rvai__sizes');
    clearBtn = panel.querySelector('.rvai__clear');

    panel.querySelector('.rvai__close').addEventListener('click', () => close());
    panel.querySelector('.rvai__form').addEventListener('submit', e => { e.preventDefault(); submit(); });
    input.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } });
    input.addEventListener('input', () => {
      grow();
      clearBtn.hidden = !input.value;
      clearTimeout(debounce);
      const q = input.value.trim();
      if (q.length < 3) { if (!q) idle(); return; }
      debounce = setTimeout(() => run({ q, live: true }), 420);
    });
    clearBtn.addEventListener('click', () => { input.value = ''; clearBtn.hidden = true; grow(); idle(); input.focus(); });
    panel.addEventListener('keydown', e => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key !== 'Tab') return;
      const focusables = [...panel.querySelectorAll('button:not([hidden]), textarea, a[href]')].filter(n => n.offsetParent);
      const first = focusables[0], last = focusables.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Clic sur le voile (hors de la fenêtre) : on ferme. Un glisser parti de la fenêtre ne compte pas.
    let pressedOnVeil = false;
    panel.addEventListener('pointerdown', e => { pressedOnVeil = e.target === panel; });
    panel.addEventListener('click', e => {
      if (e.target === panel) { if (pressedOnVeil) close(); return; }
      const t = e.target.closest('[data-idea], [data-chip], [data-size], [data-more]');
      if (!t) return;
      if (t.dataset.idea) { input.value = t.dataset.idea; clearBtn.hidden = false; grow(); submit(); }
      else if (t.dataset.chip) refine(intent => drop(intent, t.dataset.chip));
      else if (t.dataset.size) refine(intent => { intent.sizes = [t.dataset.size]; });
      else if (t.dataset.more) more();
    });
    // La seconde photo (pièce réelle) n'est chargée qu'au survol
    panel.addEventListener('mouseover', e => {
      const img = e.target.closest('.rvai-card')?.querySelector('.rvai-card__hover[data-src]');
      if (img) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
    });
  }

  const grow = () => { input.style.height = 'auto'; input.style.height = `${input.scrollHeight}px`; };
  const urlWith = q => { const u = new URL(location.href); q ? u.searchParams.set(CFG.param, q) : u.searchParams.delete(CFG.param); return u; };

  function open(q) {
    if (!panel) build();
    if (isOpen) return;
    isOpen = true;
    panel.hidden = false;
    document.documentElement.classList.add('rvai-lock');
    requestAnimationFrame(() => panel.classList.add('is-open'));
    if (!history.state?.rvai) history.pushState({ rvai: true }, '', location.href);
    if (q) { input.value = q; clearBtn.hidden = false; }
    input.focus({ preventScroll: true }); // dans le geste du clic : ouvre le clavier sur iOS
    grow();
    if (q) submit(); else if (!state.data) idle();
  }

  function close(fromHistory) {
    if (!isOpen) return;
    isOpen = false;
    ctrl?.abort();
    panel.classList.remove('is-open');
    document.documentElement.classList.remove('rvai-lock');
    setTimeout(() => { if (!isOpen) panel.hidden = true; }, calm ? 0 : 280);
    if (!fromHistory) {
      if (history.state?.rvai && history.length > 1) history.back();
      else history.replaceState(null, '', urlWith(''));
    }
    trigger.focus({ preventScroll: true });
  }
  window.addEventListener('popstate', () => { if (isOpen && !history.state?.rvai) close(true); });

  // ───────────── Appels au moteur ─────────────
  async function submit() {
    clearTimeout(debounce);
    const q = input.value.trim();
    if (q.length < 2) { input.focus(); return; }
    history.replaceState({ rvai: true }, '', urlWith(q));
    // Réponse immédiate du moteur local, puis l'IA affine en arrière-plan sans figer l'écran
    const first = await run({ q, live: true });
    if (first?.llm) run({ q, quiet: true });
  }

  function refine(mutate) {
    if (!state.data) return;
    const intent = structuredClone(state.data.intent);
    mutate(intent);
    run({ q: state.data.query, intent });
  }

  function drop(intent, path) {
    for (const part of path.split('+')) {
      const [key, index] = part.split('.');
      if (index !== undefined) intent[key].splice(+index, 1);
      else intent[key] = typeof intent[key] === 'boolean' ? false : null;
    }
  }

  // quiet : second passage par l'IA — les résultats déjà affichés restent utilisables pendant l'attente
  async function run({ q, intent, live = false, quiet = false }) {
    ctrl?.abort();
    ctrl = new AbortController();
    const mine = ctrl;
    panel.classList.remove('is-loading', 'is-thinking');
    panel.classList.add(quiet ? 'is-thinking' : 'is-loading');
    clearTimeout(skeletonTimer);
    if (!state.data && !quiet) skeletonTimer = setTimeout(skeleton, 160);
    try {
      const r = await fetch(CFG.endpoint, {
        method: 'POST', signal: mine.signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ q, intent, live }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Recherche indisponible');
      // L'IA confirme la première lecture : rien à redessiner
      if (quiet && JSON.stringify(data.intent) === JSON.stringify(state.data?.intent)) return data;
      // Chips ou taille modifiés : la correction d'orthographe déjà annoncée reste affichée
      if (intent && state.data) data.corrections = state.data.corrections;
      state = { data, shown: PAGE, catalogSize: data.catalogSize };
      render();
      return data;
    } catch (err) {
      if (err.name === 'AbortError' || quiet) return null;
      state.data = null;
      chipsBox.hidden = sizesBox.hidden = true;
      statusBox.textContent = '';
      body.innerHTML = `<div class="rvai__empty"><p>La recherche est momentanément indisponible.</p><a class="rvai__link" href="${esc(CFG.collectionUrl)}">Parcourir toute la collection</a></div>`;
    } finally {
      clearTimeout(skeletonTimer);
      if (mine === ctrl) panel.classList.remove('is-loading', 'is-thinking');
    }
  }

  // ───────────── Rendu ─────────────
  function idle() {
    ctrl?.abort();
    state.data = null;
    panel.classList.remove('is-loading', 'is-thinking', 'has-results');
    chipsBox.hidden = sizesBox.hidden = true;
    statusBox.textContent = '';
    body.innerHTML = `
      <section class="rvai__idle">
        <h3 class="rvai__subtitle">Quelques idées pour commencer</h3>
        <div class="rvai__ideas">${IDEAS.map(i => `<button type="button" class="rvai__idea" data-idea="${esc(i)}">${esc(i)}</button>`).join('')}</div>
        <dl class="rvai__hints">${HINTS.map(([t, d]) => `<div><dt>${esc(t)}</dt><dd>${esc(d)}</dd></div>`).join('')}</dl>
      </section>`;
  }

  function skeleton() {
    panel.classList.add('has-results');
    body.innerHTML = `<div class="rvai__grid" aria-hidden="true">${'<div class="rvai-card rvai-card--ghost"><span class="rvai-card__media"></span><span class="rvai-card__line"></span><span class="rvai-card__line rvai-card__line--short"></span></div>'.repeat(8)}</div>`;
  }

  const SIZES = '(min-width: 1100px) 280px, (min-width: 750px) 31vw, 46vw';
  const src = (url, w) => `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  function cardHtml(c, i) {
    const img = c.image
      ? `<img src="${esc(src(c.image, 540))}" srcset="${[360, 540, 720].map(w => `${esc(src(c.image, w))} ${w}w`).join(', ')}" sizes="${SIZES}" alt="${esc(c.title)}${c.brand ? ' ' + esc(c.brand) : ''}" loading="lazy" decoding="async">`
      : '';
    const hover = c.hover ? `<img class="rvai-card__hover" data-src="${esc(src(c.hover, 540))}" alt="" decoding="async">` : '';
    return `
      <a class="rvai-card" href="${esc(c.url)}" style="--i:${Math.min(i, 11)}">
        <span class="rvai-card__media">${img}${hover}</span>
        <span class="rvai-card__brand">${esc(c.brand ?? 'Sélection Revivr')}</span>
        <span class="rvai-card__title">${esc(c.title)}</span>
        <span class="rvai-card__meta"><span>${esc(c.size ?? 'Taille non indiquée')}</span><span class="rvai-card__price">${euros(c.price)}</span></span>
        ${c.notes?.length ? `<span class="rvai-card__note">${esc(c.notes.join(' · '))}</span>` : ''}
      </a>`;
  }

  function render() {
    const d = state.data;
    chipsBox.hidden = !d.chips.length;
    chipsBox.innerHTML = d.chips.length
      ? `<span class="rvai__chips-label">J’ai compris</span>${d.chips.map(c => `<button type="button" class="rvai-chip${c.negative ? ' rvai-chip--not' : ''}" data-chip="${esc(c.path)}" aria-label="Retirer le critère ${esc(c.label)}">${esc(c.label)}<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>`).join('')}`
      : '';

    // Fautes de frappe corrigées : on le dit, pour que la cliente sache ce qui a été cherché
    const fix = d.corrections?.length
      ? `<span class="rvai__fix">Recherche corrigée : ${d.corrections.map(c => `<strong>${esc(c.to)}</strong> au lieu de « ${esc(c.from)} »`).join(', ')}</span>`
      : '';
    if (!d.understood) statusBox.innerHTML = 'Je n’ai pas reconnu de critère précis. Indiquez par exemple une couleur, une forme de col, une taille, une marque ou un budget.';
    else if (d.total) statusBox.innerHTML = `<strong>${plural(d.total, 'pièce unique', 'pièces uniques')}</strong> ${d.total > 1 ? 'correspondent' : 'correspond'} à votre description`;
    else if (d.near.length) statusBox.innerHTML = 'Aucune pièce ne réunit tous vos critères pour le moment. Voici les plus proches.';
    else statusBox.innerHTML = 'Aucune pièce ne correspond pour le moment. Retirez un critère pour élargir la recherche.';

    statusBox.insertAdjacentHTML('afterbegin', fix);

    const askSize = d.understood && !d.intent.sizes.length && d.facets.sizes.length > 1;
    sizesBox.hidden = !askSize;
    sizesBox.innerHTML = askSize
      ? `<span class="rvai__sizes-label">Votre taille ?</span>${d.facets.sizes.map(s => `<button type="button" class="rvai-size" data-size="${esc(s.size)}" aria-label="Taille ${esc(s.size)} (${s.num}), ${plural(s.count, 'pièce', 'pièces')}">${esc(s.size)}<small>${s.num}</small></button>`).join('')}`
      : '';
    let html = '';
    if (d.results.length) {
      html += `<div class="rvai__grid" data-results>${d.results.slice(0, state.shown).map(cardHtml).join('')}</div><div class="rvai__more">${moreHtml()}</div>`;
    }
    if (d.near.length) {
      const heading = !d.understood ? 'Dernières arrivées' : d.total ? 'À un détail près' : '';
      if (heading) html += `<h3 class="rvai__subtitle rvai__subtitle--rule">${heading}</h3>`;
      html += `<div class="rvai__grid">${d.near.map(cardHtml).join('')}</div>`;
    }
    body.innerHTML = html;
    panel.classList.add('has-results');
    panel.querySelector('.rvai__inner').scrollTo({ top: 0 });
  }

  function moreHtml() {
    const d = state.data, rest = d.results.length - state.shown;
    if (rest > 0) return `<button type="button" class="rvai__btn" data-more="1">Voir plus de pièces <small>${rest} de plus</small></button>`;
    return d.total > d.results.length ? `<a class="rvai__link" href="${esc(CFG.collectionUrl)}">Parcourir toute la collection</a>` : '';
  }

  // Ajoute la page suivante sans rejouer l'animation des cartes déjà affichées
  function more() {
    const from = state.shown;
    state.shown += PAGE;
    const grid = body.querySelector('[data-results]');
    grid.insertAdjacentHTML('beforeend', state.data.results.slice(from, state.shown).map(cardHtml).join(''));
    body.querySelector('.rvai__more').innerHTML = moreHtml();
    grid.children[from]?.focus({ preventScroll: true });
  }

  // Lien direct ou retour arrière : /?ia=un col V bleu marine rouvre la recherche
  const initial = new URL(location.href).searchParams.get(CFG.param);
  if (initial) open(initial);
})();
